import type { Cabecera } from '@features/seguimiento/cabecera';
import { CABECERAS } from '@features/pago/copy-retorno';
import { Desenlace } from '@features/pago/desenlace';

/**
 * Lo que la renovacion dice y la matricula nueva no (SPEC-016).
 *
 * Aparte del componente, como el resto del copy del bloque 6: se lee junto y se
 * prueba sin DOM.
 */

/**
 * Los 20 minutos de la Logic App, dichos antes de pagar.
 *
 * **Es un limite de producto y hay que contarlo.** La Logic App de renovaciones
 * borra la membresia que lleve mas de 20 minutos sin resolverse y deja registro
 * de solicitud abandonada; quien tarde mas pierde la renovacion y empieza de
 * cero. **En matricula nueva no aplica** —alli el pago puede esperar dias— y
 * reutilizar la pantalla sin este aviso dejaria a la persona sin saberlo.
 */
export const AVISO_DE_LA_VENTANA =
  'Tienes 20 minutos para completar el pago. Si se pasa el tiempo tendrás que buscar tu documento y elegir de nuevo.';

/**
 * La salida de la vuelta de la pasarela en una renovacion.
 *
 * **No es *"ver el estado de mi matricula"***, que es lo que dice la matricula
 * nueva: alli hay un radicado y una linea de tiempo esperando, y aqui no hay
 * nada que aprobar ni que consultar. La activacion la hace la Logic App en los
 * tres minutos siguientes, sin que nadie tenga que mirar.
 */
export const VOLVER_AL_INICIO = 'Volver al inicio';

/**
 * Lo que dice la vuelta de la pasarela en una renovacion (SPEC-016, tras lab).
 *
 * **Nace de un caso real**: el pago salio aprobado y la pantalla dijo *"El
 * estado del tramite lo confirma en un momento; entra cuando quieras a
 * mirarlo"*, con un boton debajo que lleva al inicio. Ese texto lo escribio
 * SPEC-013 para la matricula nueva, donde hay radicado y linea de tiempo que
 * mirar; **en una renovacion no hay nada de eso**, y mandar a mirar un estado
 * que no existe es peor que no decir nada.
 *
 * **Se redefinen dos de los cuatro y los otros dos se heredan**: `esperando` y
 * `rechazado` hablan del banco y del cobro, que es igual en los dos tramites.
 * Copiarlos aqui seria tener el mismo texto en dos sitios, que es como empiezan
 * a divergir.
 */
export const CABECERAS_DE_RENOVACION: Record<Desenlace, Cabecera> = {
  ...CABECERAS,

  /**
   * **No se dice "matriculado"**, igual que en la matricula nueva y por la misma
   * razon: quien activa la membresia, suma el cupo del grupo y dispara el correo
   * es la Logic App de renovaciones, que sondea cada tres minutos. Hay una
   * ventana en la que el pago ya esta y la matricula todavia no.
   *
   * Lo que si se puede prometer es el correo, porque bajar `comunications.welcome`
   * es exactamente lo que lo dispara.
   */
  aprobado: {
    titulo: '¡Listo, recibimos tu pago!',
    icono: 'ph-fill ph-check-circle',
    tono: 'exito',
    detalle: [
      {
        texto:
          'Estamos activando tu matrícula. En unos minutos te llega el correo de confirmación; si no lo ves, revisa el correo no deseado.',
      },
    ],
  },

  /** Sin estado que consultar, lo unico que sirve es el canal de la Escuela. */
  error: {
    titulo: 'No pudimos confirmar tu pago',
    icono: 'ph-fill ph-warning-circle',
    tono: 'detenida',
    detalle: [
      {
        texto:
          'No logramos consultar cómo quedó la transacción. Si el cobro te aparece en el banco y no te llega el correo de confirmación, escríbenos por WhatsApp y lo revisamos.',
      },
    ],
  },
};

/**
 * La espera agotada, sin el enlace del correo.
 *
 * El de la matricula nueva dice *"entra otra vez por el enlace de tu correo"*, y
 * **una renovacion no tiene enlace**: se vuelve buscando el documento otra vez.
 */
export const ESPERA_AGOTADA_DE_RENOVACION =
  'Tu banco se está tardando más de lo normal. Si el cobro te aparece y en unos minutos no te llega el correo de confirmación, escríbenos por WhatsApp con el comprobante y lo revisamos.';

/**
 * Como se llama el numero que la vuelta del pago ensena en una renovacion.
 *
 * **Es el `paymentId`** (usuario, 2026-09-14), y no un numero nuestro: es por donde
 * el gestor lo busca en el portal —su *"Trazabilidad del pago"* va por ahi—, asi
 * que si la persona llama, el numero que trae sirve para encontrarla.
 *
 * **Una renovacion no radica nada**, y por eso hace falta: quien pagaba se iba sin
 * ningun numero anotado. La matricula nueva tiene su radicado y esta pantalla la
 * lleva hasta el.
 */
export const NUMERO_DEL_PAGO = 'Número de tu pago';
